import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, rupee } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Minus, Plus, ShieldCheck, Truck, RefreshCw, ImageDown } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState("");
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [ep, setEp] = useState(null); // {order, qr}
  const [revs, setRevs] = useState({ reviews: [], count: 0, avg: 0 });
  const [myRating, setMyRating] = useState(5);
  const [myText, setMyText] = useState("");
  const [epForm, setEpForm] = useState({ name: "", email: "", phone: "", address: "" });

  const submitReview = async () => {
    try {
      await api.post("/reviews", { product_id: p.id, rating: myRating, text: myText });
      toast.success("Review submitted — thank you");
      setMyText("");
      const { data } = await api.get(`/products/${p.id}/reviews`);
      setRevs(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit review"); }
  };

  const buyEpicture = async () => {
    if (!user) { nav("/login"); return; }
    setEp("form");
  };
  const confirmEpicture = async () => {
    try {
      const { data } = await api.post("/epicture/purchase", { product_id: p.id, ...epForm });
      const qr = await api.get(`/payments/upi-qr?amount=1&order_note=${encodeURIComponent("LEE BAG e-Picture " + data.order_id)}`);
      setEp({ order: data, qr: qr.data });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => { setP(r.data); setColor(r.data.colors?.[0] || ""); });
    api.get(`/products/${id}/reviews`).then((r) => setRevs(r.data)).catch(() => {});
  }, [id, api]);
  if (!p) return <div className="p-20 text-center">Loading…</div>;

  const price = p.discount_price || p.price;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 fade-up">
      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <div className="product-image !aspect-[4/5]"><img src={p.images?.[img]} alt={p.name} /></div>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {p.images?.map((im, i) => (
              <button key={i} onClick={() => setImg(i)} className={`aspect-square overflow-hidden ${i === img ? "ring-1 ring-[#C5A059]" : ""}`} data-testid={`pd-thumb-${i}`}>
                <img src={im} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
        <div className="md:sticky md:top-28 self-start">
          <p className="eyebrow">{p.category}</p>
          <h1 className="font-serif text-3xl md:text-5xl mt-3">{p.name}</h1>
          <div className="mt-5 text-xl">
            {p.discount_price ? (
              <><span className="text-[#5B1C22] font-medium">{rupee(p.discount_price)}</span> <span className="line-through text-[#8C867D] text-sm ml-3">{rupee(p.price)}</span></>
            ) : <span className="font-medium">{rupee(p.price)}</span>}
          </div>
          <p className="mt-2 text-xs text-[#8C867D]">or EMI from {rupee(Math.round(price/6))}/mo. No-Cost EMI available at checkout.</p>
          <p className="mt-6 text-sm text-[#55524D] leading-relaxed">{p.description}</p>

          {p.colors?.length > 0 && (
            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.25em] mb-3">Colour · <span className="text-[#8C867D]">{color}</span></p>
              <div className="flex gap-2">
                {p.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} className={`px-4 py-2 text-xs uppercase tracking-widest border ${color === c ? "border-[#121212]" : "border-[#E2DDD5]"}`} data-testid={`pd-color-${c}`}>{c}</button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-[#121212]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" data-testid="pd-qty-minus"><Minus size={14} /></button>
              <span className="px-4 text-sm">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3" data-testid="pd-qty-plus"><Plus size={14} /></button>
            </div>
            <button onClick={() => { add(p, qty); toast.success("Added to bag"); }} className="btn-lux flex-1" data-testid="pd-add-to-cart">Add to Bag</button>
          </div>
          <button onClick={buyEpicture} className="btn-lux-outline w-full mt-3 flex items-center justify-center gap-2" data-testid="pd-epicture-buy">
            <ImageDown size={14} /> Get e-Picture · ₹1 (Online Only)
          </button>
          <p className="text-[10px] text-[#8C867D] mt-1">Download the LEE BAG branded photo of this product to your phone or laptop. Unlocks after payment confirmation.</p>
          <div className="mt-3 text-xs text-[#8C867D]">
            {p.stock > 5 ? "In Stock" : p.stock > 0 ? `Only ${p.stock} left` : "Out of Stock"}
          </div>

          <div className="mt-10 space-y-3 text-xs text-[#55524D] border-t border-[#E2DDD5] pt-6">
            <div className="flex items-center gap-3"><ShieldCheck size={14} className="text-[#C5A059]" /> Signed & QR-authenticated LEE BAG certificate</div>
            <div className="flex items-center gap-3"><Truck size={14} className="text-[#C5A059]" /> Complimentary luxury shipping</div>
            <div className="flex items-center gap-3"><RefreshCw size={14} className="text-[#C5A059]" /> 14-day returns · lifetime craft warranty</div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 text-xs">
            <div><p className="uppercase tracking-widest text-[#8C867D]">Material</p><p className="mt-1">{p.material}</p></div>
            <div><p className="uppercase tracking-widest text-[#8C867D]">Dimensions</p><p className="mt-1">{p.dimensions}</p></div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-20 border-t border-[#E2DDD5] pt-12" data-testid="reviews-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="eyebrow">Reviews</p>
            <h2 className="font-serif text-2xl md:text-4xl mt-2">What Collectors Say</h2>
          </div>
          <div className="text-right">
            <div className="text-[#C5A059] text-lg" data-testid="review-avg">{"★".repeat(Math.round(revs.avg))}{"☆".repeat(5 - Math.round(revs.avg))}</div>
            <p className="text-xs text-[#8C867D]">{revs.avg} / 5 · {revs.count} review{revs.count === 1 ? "" : "s"}</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            {revs.reviews.length === 0 && <p className="text-sm text-[#8C867D]">No reviews yet — be the first collector to share.</p>}
            {revs.reviews.map((r) => (
              <div key={r.id} className="border border-[#E2DDD5] p-5 bg-white" data-testid={`review-item-${r.id}`}>
                <div className="flex justify-between">
                  <p className="text-sm font-medium">{r.customer_name}</p>
                  <span className="text-[#C5A059]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                </div>
                {r.text && <p className="text-sm text-[#55524D] mt-2 leading-relaxed">{r.text}</p>}
                <p className="text-[10px] text-[#8C867D] mt-2 uppercase tracking-widest">{new Date(r.created_at).toLocaleDateString("en-IN")}</p>
              </div>
            ))}
          </div>
          {user && (
            <div className="border border-[#E2DDD5] p-6 bg-white h-fit">
              <p className="eyebrow mb-4">Write a Review</p>
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setMyRating(n)} className={`text-2xl ${myRating >= n ? "text-[#C5A059]" : "text-[#E2DDD5]"}`} data-testid={`review-star-${n}`}>★</button>
                ))}
              </div>
              <textarea value={myText} onChange={(e) => { if (e.target.value.trim().split(/\s+/).filter(Boolean).length <= 1000) setMyText(e.target.value); }} rows={5} placeholder="Share your experience (up to 1000 words)…" className="w-full border border-[#E2DDD5] px-3 py-2 text-sm" data-testid="review-text" />
              <p className="text-[10px] text-[#8C867D] mt-1">{myText.trim() ? myText.trim().split(/\s+/).length : 0}/1000 words</p>
              <button onClick={submitReview} className="btn-lux mt-4 w-full" data-testid="review-submit">Submit Review</button>
              <p className="text-[10px] text-[#8C867D] mt-2">Only verified buyers can review.</p>
            </div>
          )}
        </div>
      </section>
      {ep && <EpictureModal ep={ep === "form" ? null : ep} onClose={() => setEp(null)} form={epForm} setForm={setEpForm} onConfirm={confirmEpicture} />}
    </div>
  );
}

export function EpictureModal({ ep, onClose, form, setForm, onConfirm }) {
  const filled = form.name && form.email && form.phone && form.address;
  return (
    <div className="fixed inset-0 z-[100] bg-[#121212]/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white p-8 max-w-sm w-full text-center my-8" onClick={(e) => e.stopPropagation()} data-testid="epicture-pay-modal">
        <p className="eyebrow">e-Picture</p>
        <p className="font-serif text-2xl mt-2">{rupee(1)} only</p>
        <div className="text-left space-y-2 mt-4">
          <input placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#E2DDD5] px-3 py-2 text-sm" data-testid="epic-form-name" />
          <input placeholder="Gmail ID (e-picture will be emailed here)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#E2DDD5] px-3 py-2 text-sm" data-testid="epic-form-email" />
          <input placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-[#E2DDD5] px-3 py-2 text-sm" data-testid="epic-form-phone" />
          <input placeholder="Full Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border border-[#E2DDD5] px-3 py-2 text-sm" data-testid="epic-form-address" />
        </div>
        {!ep ? (
          <>
            <button onClick={onConfirm} disabled={!filled} className="btn-lux w-full mt-4 disabled:opacity-40" data-testid="epic-form-submit">Continue to Pay ₹1</button>
            <button onClick={onClose} className="text-xs uppercase tracking-widest text-[#8C867D] mt-3">Cancel</button>
          </>
        ) : (
          <>
            <p className="text-xs text-[#8C867D] mt-3">Pay to <span className="font-mono">{ep.qr.upi_id}</span></p>
            <img src={ep.qr.qr_image} alt="₹1 UPI QR" className="w-48 h-48 mx-auto mt-4 border border-[#E2DDD5]" data-testid="epicture-qr" />
            <a href={ep.qr.upi_url} className="btn-lux-gold mt-4 inline-flex" data-testid="epicture-upi-button">Open UPI App & Pay ₹1</a>
            <button onClick={() => { toast.success("Payment noted — your e-Picture will be emailed to you once payment is confirmed"); onClose(); }} className="btn-lux w-full mt-4" data-testid="epicture-paid-confirm">Done</button>
          </>
        )}
      </div>
    </div>
  );
}
